/**
 * The rail's "quiet tree" look, shared by every row in it (tokens, their
 * pages, chain entries, global links, saved views) and by the hover flyout.
 * Text is neutral; hover is a fill; the current page is the one row with a
 * 2px accent bar at its left edge, so hover and current never look alike.
 * Rail links are left out of the global accent link rule (styles/index.css,
 * [data-rail]), so these colours apply.
 *
 * In the phone drawer a token's pages sit two to a row (`guideCls`), each
 * a 32px cell with the bar at its own left edge; saved views and every other
 * row keep the full width.
 */

/**
 * A row's layout and states, without its text colour (callers add one, so
 * two colour utilities never compete). Focus is an inset ring, which the
 * rail's overflow cannot clip; `!` beats the global unlayered outline. In the
 * phone drawer rows are at least 32px tall, for touch.
 */
export const focusCls = "focus-visible:outline-none! focus-visible:shadow-[inset_0_0_0_1.5px_var(--focus)]";
export const rowCls = `relative flex min-w-0 items-baseline gap-1 rounded-sm px-2 py-0.5 whitespace-nowrap hover:bg-bg-hover hover:text-fg narrow:min-h-8 narrow:items-center ${focusCls}`;

/** The accent bar marking the current page (its left edge set by `barAt`); the row must be `relative`. */
const barCls =
  "before:absolute before:top-[3px] before:bottom-[3px] before:w-0.5 before:rounded-[1px] before:bg-accent before:content-['']";
const currentCls = `font-semibold text-fg ${barCls}`;

/**
 * Where the bar sits: the row's edge, or on the guide line of a token's pages
 * (in the phone drawer's grid, at the cell's left edge instead).
 */
const barAt = { row: "before:left-0", guide: "before:-left-[3px] narrow:before:left-0" } as const;

/** A link row, current or not; `under: "guide"` for a token's pages. */
export const linkCls = ({ isActive }: { isActive: boolean }, under: keyof typeof barAt = "row") =>
  `${rowCls} ${isActive ? `${currentCls} ${barAt[under]}` : "text-fg-muted"}`;

/** The bar at a row's left edge, for rows that are not `linkCls` links (saved views). */
export const rowBarCls = `${barCls} ${barAt.row}`;

/**
 * A token's pages hang under a 1px guide line; in the phone drawer they fill
 * a two-column grid, row by row in DOM order (so keyboard order is left cell,
 * then right cell). A row that is not a page spans both columns (`fullCls`).
 */
export const guideCls = "ml-3 border-l border-border pl-0.5 narrow:grid narrow:grid-cols-2";
export const fullCls = "narrow:col-span-2";

/** Text that gives way first when a row is narrow. */
export const truncCls = "min-w-0 flex-1 truncate";
